import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, LayoutGrid, Plus, Search, X } from 'lucide-react';
import AskIcon from 'shared/components/ui/AskIcon';
import BrandLoader from 'shared/components/ui/BrandLoader';
import { openAsk } from 'shared/askDraft';
import { activitySearchInputClass, activitySearchWrapperClass } from 'features/activity/styles/dataTableStyles';
import { createAgentFromTemplate, listAgentTemplateCatalog } from 'features/agents/api/agentsApi';
import { TemplateIcon } from 'features/agents/templates/templateIcons';
import TemplateCard, { DEFAULT_NEEDS, TRIGGERS } from 'features/agents/components/TemplateCard';
import TemplateParamsForm, { blockersFor, initialValues, useParamsContext } from 'features/agents/components/TemplateParamsForm';

/**
 * "New agent": describe it to Lucid, start from scratch, or pick a template
 * from the library. A template asks for the few choices it needs in a second
 * step, then lands in the editor on a draft; nothing runs until it is
 * published there.
 */

const MOTION = `
.agent-template-dialog { animation: agentDialogIn 160ms cubic-bezier(0.16, 1, 0.3, 1) both; }
.agent-template-card { animation: agentCardIn 200ms cubic-bezier(0.16, 1, 0.3, 1) both; }
@keyframes agentDialogIn { from { opacity: 0; transform: translateY(-8px) scale(0.985); } to { opacity: 1; transform: none; } }
@keyframes agentCardIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .agent-template-dialog, .agent-template-card { animation: none; }
}`;

const OPTION = 'flex h-14 flex-1 items-center gap-3 rounded-xl border border-[color:var(--border-default)] bg-surface px-3 text-left transition-colors hover:border-[color:var(--border-strong)] hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])';

export function matchesTemplate(t, q, needsLabels = DEFAULT_NEEDS) {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const hay = [
    t.name, t.description, t.category, ...(t.tags || []),
    TRIGGERS[t.trigger]?.label || t.trigger,
    ...(t.needs || []).map((n) => needsLabels[n] || n),
  ].join(' ').toLowerCase();
  return s.split(/\s+/).every((w) => hay.includes(w));
}

/**
 * `initialQuery` opens the gallery already searched (the Datasets page opens it on
 * "dataset"); `prefill` seeds a picked template's params (e.g. its dataset_id).
 */
export default function NewAgentDialog({ open, onClose, initialQuery = '', prefill = null }) {
  const navigate = useNavigate();
  const { team } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [picked, setPicked] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState('');
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const ctx = useParamsContext(picked);

  const load = useCallback(() => {
    setError(null);
    return Promise.resolve(listAgentTemplateCatalog())
      .then(setData)
      .catch((e) => setError(e?.response?.data?.error || e?.message || 'Request failed'));
  }, []);

  useEffect(() => {
    if (!open || data) return;
    load();
  }, [open, data, load]);

  useEffect(() => {
    if (!open) return undefined;
    setQ(initialQuery || ''); setCat('all'); setPicked(null); setBusy(false); setCreateError('');
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab' || !panelRef.current) return;
    const items = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const onGridKey = (e) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!keys[e.key]) return;
    const cards = [...e.currentTarget.closest('[data-template-list]').querySelectorAll('[data-template-card]')];
    const i = cards.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    cards[Math.min(cards.length - 1, Math.max(0, i + keys[e.key]))].focus();
  };

  const categories = useMemo(() => data?.categories || [], [data]);
  const all = useMemo(() => data?.templates || [], [data]);
  const needsLabels = useMemo(() => ({ ...DEFAULT_NEEDS, ...(data?.needs || {}) }), [data]);
  const shown = useMemo(
    () => all.filter((t) => (cat === 'all' || t.category === cat) && matchesTemplate(t, q, needsLabels)),
    [all, cat, q, needsLabels],
  );
  const counts = useMemo(() => {
    const m = { all: all.length };
    for (const t of all) m[t.category] = (m[t.category] || 0) + 1;
    return m;
  }, [all]);
  const browsing = !q.trim() && cat === 'all';
  const featured = useMemo(() => (browsing ? all.filter((t) => t.featured) : []), [all, browsing]);
  const rest = useMemo(() => (browsing ? shown.filter((t) => !t.featured) : shown), [shown, browsing]);

  const pick = (t) => {
    const base = initialValues(t);
    for (const p of t?.params || []) if (prefill && prefill[p.key] != null) base[p.key] = prefill[p.key];
    setPicked(t); setValues(base); setCreateError('');
  };
  const blockers = picked ? blockersFor(picked, values, ctx) : [];

  const create = async () => {
    setBusy(true);
    setCreateError('');
    try {
      const out = await createAgentFromTemplate(picked.id, values);
      onClose();
      navigate(`/${encodeURIComponent(team || '')}/agents/${out.workflow.id}`);
    } catch (e) {
      setCreateError(e?.response?.data?.error || e?.message || 'Could not create the agent.');
      setBusy(false);
    }
  };

  const withLucid = () => { onClose(); openAsk('Create an agent that '); };
  const scratch = () => { onClose(); navigate(`/${encodeURIComponent(team || '')}/agents/new`); };

  if (!open) return null;

  const grid = (items, offset = 0) => (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3" onKeyDown={onGridKey}>
      {items.map((t, i) => <TemplateCard key={t.id} template={t} needsLabels={needsLabels} onPick={pick} index={i + offset} />)}
    </div>
  );

  const el = (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[7vh]"
      style={{ background: 'color-mix(in srgb, var(--text-primary) 40%, transparent)' }}
      role="dialog" aria-modal="true" aria-labelledby="new-agent-title"
      onKeyDown={onKeyDown}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <style>{MOTION}</style>
      <div ref={panelRef} className="agent-template-dialog flex max-h-[80vh] w-full max-w-[860px] flex-col overflow-hidden rounded-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-2xl">
        <div className="flex items-center gap-2 border-b border-[color:var(--border-subtle)] px-4 py-3">
          {picked && (
            <button type="button" onClick={() => { setPicked(null); setCreateError(''); }} aria-label="Back"
              className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"><ChevronLeft size={16} /></button>
          )}
          <h2 id="new-agent-title" className="text-[14px] font-semibold text-[color:var(--text-primary)]">{picked ? picked.name : 'New agent'}</h2>
          <div className="flex-1" />
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"><X size={16} /></button>
        </div>

        {picked ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4" data-testid="template-params">
              <div className="mx-auto flex max-w-[520px] flex-col gap-4">
                <p className="text-[12px] leading-snug text-[color:var(--text-muted)]">{picked.description}</p>
                {picked.setup && <p className="text-[12px] leading-snug text-[color:var(--text-secondary)]">{picked.setup}</p>}
                <TemplateParamsForm template={picked} values={values} onChange={setValues} ctx={ctx} />
                {createError && <div className="text-[12px] text-danger" role="alert">{createError}</div>}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-[color:var(--border-subtle)] px-4 py-3">
              <span className="min-w-0 truncate text-[11.5px] text-[color:var(--text-muted)]" data-testid="template-blockers">
                {blockers[0] || 'Opens as a draft; publish it from the editor.'}
              </span>
              <button type="button" onClick={create} disabled={busy || blockers.length > 0}
                className="inline-flex h-8 shrink-0 items-center rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50">
                {busy ? 'Creating' : 'Create agent'}
              </button>
            </div>
          </>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-template-list>
            <div className="flex flex-col gap-2 px-4 pb-1 pt-3 sm:flex-row">
              <button type="button" onClick={withLucid} className={OPTION}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-base)] text-brand"><AskIcon size={16} /></span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-[color:var(--text-primary)]">Create with Lucid</span>
                  <span className="block truncate text-[11.5px] text-[color:var(--text-muted)]">Describe it and Lucid builds it</span>
                </span>
              </button>
              <button type="button" onClick={scratch} className={OPTION}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-base)] text-[color:var(--text-secondary)]"><Plus size={16} aria-hidden /></span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-[color:var(--text-primary)]">From scratch</span>
                  <span className="block truncate text-[11.5px] text-[color:var(--text-muted)]">Start with an empty canvas</span>
                </span>
              </button>
            </div>

            <div className="flex items-center gap-3 px-4 pb-2 pt-4">
              <h3 className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Templates</h3>
              <label className={`${activitySearchWrapperClass} w-[260px] max-w-full focus-within:border-[color:var(--border-strong)]`}>
                <Search size={13} className="shrink-0 text-[color:var(--text-muted)]" aria-hidden />
                <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates"
                  aria-label="Search templates" className={activitySearchInputClass} />
                {q && <button type="button" onClick={() => setQ('')} aria-label="Clear search" className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><X size={12} /></button>}
              </label>
            </div>

            <div className="flex min-h-0 flex-1 border-t border-[color:var(--border-subtle)]">
              <nav className="sticky top-0 hidden w-[196px] shrink-0 self-start flex-col gap-0.5 border-r border-[color:var(--border-subtle)] px-2 py-2.5 sm:flex" aria-label="Categories">
                {[{ id: 'all', label: 'All' }, ...categories].map((c) => {
                  const on = cat === c.id;
                  return (
                    <button key={c.id} type="button" role="radio" aria-checked={on} onClick={() => setCat(c.id)}
                      className={`flex h-8 items-center gap-2 rounded-lg px-2 text-left text-[12px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)] ${on
                        ? 'bg-surface-hover font-medium text-[color:var(--text-primary)]'
                        : 'text-[color:var(--text-secondary)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]'}`}>
                      <span className={on ? 'text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}>
                        {c.id === 'all' ? <LayoutGrid size={14} aria-hidden /> : <TemplateIcon name={c.icon} size={14} />}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{c.label}</span>
                      <span className="shrink-0 text-[10.5px] tabular-nums text-[color:var(--text-muted)]">{counts[c.id] || 0}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="min-w-0 flex-1 px-4 py-3" data-testid="template-list">
                {error && (
                  <div className="text-[12px] text-danger" role="alert">
                    Templates could not load: {error}{' '}
                    <button type="button" onClick={load} className="font-medium text-[color:var(--text-primary)] underline-offset-2 hover:underline">Retry</button>
                  </div>
                )}
                {!data && !error && <div className="flex justify-center py-10"><BrandLoader label="Loading templates" /></div>}
                {data && shown.length === 0 && (
                  <div className="py-10 text-center text-[12px] text-[color:var(--text-muted)]">
                    {q.trim() ? `No template matches "${q.trim()}".` : 'No templates here yet.'}
                  </div>
                )}
                {featured.length > 0 && (
                  <section className="mb-5" aria-label="Start here">
                    <h3 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Start here</h3>
                    {grid(featured)}
                  </section>
                )}
                {rest.length > 0 && (
                  <section aria-label={browsing ? 'All templates' : 'Results'}>
                    {featured.length > 0 && <h3 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">All templates</h3>}
                    {grid(rest, featured.length)}
                  </section>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  return createPortal(el, document.body);
}
